import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { useLocationDetails } from '~hooks/useLocationDetails';
import { useLayout } from '~hooks/useLayout';
import { useSeo } from '~hooks/useSeo';
import NotificationsActions from '~redux/notifications';
import { UserContext } from '~contexts/user';
import PromptService from '~services/prompt';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { Suspense, ErrorBoundary } from '~components/suspense';
import FormSkeleton from '~components/forms/skeleton';
import Hero from '~components/hero';
import Loader from '~components/loader';

import WishAddress from '../form/address';
import WishFields from '../form/fields';
import { useDefaultWish } from '../default-wish';

import { getValidatedQueryString } from '~helpers/queryUtils';

const EditWish = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const { user, isLoading, updateCustomer } = useContext(UserContext);
  const { wish } = user;
  console.log(wish)
  const defaultWish = useDefaultWish();
  const wishToBeEdited = !wish || !wish.address || !wish.address.place_id 
    ? defaultWish 
    : { ...defaultWish, ...wish, timeframes: wish.timeframes?.length ? wish.timeframes : defaultWish.timeframes };
  if (wish.personalNote) {
    wishToBeEdited.personalNote = wish.personalNote;
  }

  useLayout({ displayCities: false });
  useSeo(intl.formatMessage({ id: 'containers.wishes.edit.title', defaultMessage: 'Edit your wishes' }));
  const location = useLocation();

  const [showFields, setShowFields] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [defaultPrice, setDefaultPrice] = useState(wishToBeEdited?.maximumRate?.amount || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    locationDetails,
    pricingDetails,
    isLoading: isLoadingLocationDetails,
    requestLocationByID,
    fetchCustomerPaymentForLocation,
  } = useLocationDetails(wish.address?.place_id || null);

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingInitial(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (locationDetails && pricingDetails) {
      if (!defaultPrice) {
        setDefaultPrice((pricingDetails.maxPrice - pricingDetails.minPrice) * 0.75 + pricingDetails.minPrice);
      }
      setShowFields(true);
    }
  }, [locationDetails, pricingDetails]);

  const handleAddressSubmit = async (newAddress) => {
    await requestLocationByID(newAddress.place_id);
    const customerPaymentDetails = await fetchCustomerPaymentForLocation(user.personId);
    if (customerPaymentDetails && customerPaymentDetails.length > 0) {
      PromptService.alert({
        title: intl.formatMessage({
          id: 'containers.wishes.edit.location-change-prompt.title',
          defaultMessage: 'Changing the address',
        }),
        content: intl.formatMessage({
          id: 'containers.wishes.edit.location-change-prompt.content',
          defaultMessage:
            'Changing your address might result in a an additional payment request just for the new workers for your new address.',
        }),
      }).then(null);
    }
  };

  const handleSubmit = useCallback(async (w) => {
    try {
      setIsSubmitting(true);
      const publishedQueryParam = queryString.parse(getValidatedQueryString(location.search)).published;
      const published = publishedQueryParam || w.published;

      const newUser = { ...user, wish: { ...w, address: locationDetails, published } };
      await updateCustomer(newUser);
      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({
            id: 'containers.wishes.edit.notification',
            defaultMessage: 'Your wishes have been saved successfully!',
          })
        )
      );
      setIsSubmitting(false);
      history.push(`/workers?locationId=${locationDetails.locationId}` || '/account');
    } catch (err) {
      console.log('Error while updating user', err);
    }
  });

  return (
    <div className="c-account__main l-container">
      <Hero.Wrapper>
        <Hero.Title>
          {intl.formatMessage({ id: 'containers.wishes.edit.headline', defaultMessage: 'My wishes' })}
        </Hero.Title>
      </Hero.Wrapper>
      <Suspense fallback={<FormSkeleton align="center" className="l-mt2" />} isLoading={isLoading}>
        <ErrorBoundary fallback={<h2 className="l-error">Could not fetch your wish</h2>}>
          <div className={cx('c-form--intake', { 'c-form--intake-show-fields': showFields })}>
            <WishAddress
              defaultAddress={locationDetails}
              onSubmit={handleAddressSubmit}
              isDataLoading={isLoadingInitial}
            />
            {!showFields && isLoadingLocationDetails ? (
              <div className="l-center l-mt2">
                <Loader />
              </div>
            ) : null}
            {showFields && (
              <WishFields
                wish={wishToBeEdited}
                pricingDetails={pricingDetails}
                defaultPrice={defaultPrice}
                translationsContext="edit"
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </ErrorBoundary>
      </Suspense>
    </div>
  );
};

export default EditWish;
